import { useState } from "react";
import { motion, useReducedMotion, useScroll, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowDown, Copy, Mail, MessageCircle } from "lucide-react";
import { useLang, WHATSAPP_URL, EMAIL, EMAIL_URL, HERO_BG } from "../i18n";
import { scrollToId, trackEvent } from "../lib";

const EASE = [0.16, 1, 0.3, 1];

export const Hero = () => {
  const { t } = useLang();
  const [copied, setCopied] = useState(false);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll();
  const bgY = useTransform(scrollYProgress, [0, 0.35], [0, 140]);
  const bgOpacity = useTransform(scrollYProgress, [0, 0.35], [0.55, 0.15]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rX = useSpring(useTransform(my, [-0.5, 0.5], [5, -5]), { stiffness: 120, damping: 16 });
  const rY = useSpring(useTransform(mx, [-0.5, 0.5], [-5, 5]), { stiffness: 120, damping: 16 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      data-testid="hero"
      onMouseMove={onMove}
      className="relative flex min-h-screen items-center overflow-hidden px-5 pb-16 pt-28 sm:px-8"
    >
      <motion.div
        style={reduce ? {} : { y: bgY, opacity: bgOpacity }}
        className="pointer-events-none absolute inset-0"
        aria-hidden="true"
      >
        <img src={HERO_BG} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,#050505_72%)]" />
        <div className="grid-bg absolute inset-0 opacity-60" />
      </motion.div>

      <div className="relative z-10 mx-auto grid w-full max-w-7xl items-center gap-16 lg:grid-cols-[1.15fr_0.85fr]">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="font-mono text-xs font-semibold uppercase tracking-[0.3em] text-volt"
          >
            {t.hero.tag}
          </motion.p>

          <h1 className="mt-8 font-display text-[12vw] font-black leading-[0.95] tracking-tighter sm:text-6xl lg:text-7xl">
            {t.hero.lines.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.95, delay: 0.15 + i * 0.13, ease: EASE }}
                >
                  {line.map((tok, j) => (
                    <span key={j} className={tok.hl ? "text-volt" : ""}>
                      {tok.t}
                    </span>
                  ))}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 1, delay: 0.7, ease: EASE }}
            className="mt-8 h-px w-40 origin-left bg-gradient-to-r from-volt to-transparent"
          />

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.8 }}
            className="mt-8 max-w-xl text-base leading-relaxed text-mist sm:text-lg"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.95 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-scroll"
              onClick={() => {
                trackEvent("explore_cycle_click", { section: "hero" });
                scrollToId("#ciclo");
              }}
              className="inline-flex items-center gap-2.5 rounded-full bg-volt px-7 py-3.5 font-display font-bold text-black transition-shadow hover:shadow-[0_0_40px_-8px_rgba(0,229,255,0.8)]"
            >
              {t.hero.ctaScroll}
              <ArrowDown size={18} />
            </button>
            <a
              data-testid="hero-cta-whatsapp"
              href={WHATSAPP_URL}
              target="_blank"
              rel="noreferrer"
              onClick={() => trackEvent("whatsapp_click", { section: "hero" })}
              className="inline-flex items-center gap-2.5 rounded-full border border-white/25 px-7 py-3.5 font-display font-bold text-white transition-colors hover:bg-white hover:text-black"
            >
              <MessageCircle size={18} />
              {t.hero.ctaWpp}
            </a>
            <div className="inline-flex items-center gap-1 rounded-full border border-white/25 py-2 pl-5 pr-2">
              <button
                data-testid="hero-cta-email"
                onClick={() => {
                  try {
                    navigator.clipboard.writeText(EMAIL);
                  } catch (e) {}
                  trackEvent("email_copy", { section: "hero" });
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
                className="inline-flex items-center gap-2 font-mono text-sm text-white/85 transition-colors hover:text-volt"
              >
                {copied ? t.hero.emailCopied : EMAIL}
                <Copy size={14} className="opacity-70" />
              </button>
              <a
                data-testid="hero-cta-email-mailto"
                href={EMAIL_URL}
                aria-label={t.cta.email}
                onClick={() => trackEvent("email_click", { section: "hero" })}
                className="rounded-full p-2 text-white/60 transition-colors hover:bg-white hover:text-black"
              >
                <Mail size={15} />
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.15 }}
            className="mt-12 flex flex-wrap gap-2"
          >
            {t.hero.chips.map((c) => (
              <span
                key={c}
                className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 font-mono text-[11px] tracking-wider text-white/60"
              >
                {c}
              </span>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.6, ease: EASE }}
          className="relative hidden lg:block"
          style={{ perspective: 1000 }}
        >
          <motion.div
            style={reduce ? {} : { rotateX: rX, rotateY: rY, transformStyle: "preserve-3d" }}
            className="glow-volt rounded-2xl border border-volt/25 bg-ink-900/80 backdrop-blur-md"
            data-testid="hero-terminal"
          >
            <div className="flex items-center gap-2 border-b border-white/10 px-5 py-3.5">
              <span className="h-2.5 w-2.5 rounded-full bg-[#FF5F57]" />
              <span className="h-2.5 w-2.5 rounded-full bg-[#FEBC2E]" />
              <span className="h-2.5 w-2.5 rounded-full bg-[#28C840]" />
              <span className="ml-3 font-mono text-[11px] tracking-widest text-white/50">
                {t.hero.termTitle}
              </span>
            </div>
            <div className="space-y-2 px-5 py-5 font-mono text-[13px] leading-6">
              {t.hero.term.map((l, i) => (
                <motion.p
                  key={i}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1 + i * 0.22 }}
                  className={i === 0 ? "text-white/90" : "text-white/55"}
                >
                  {l}
                </motion.p>
              ))}
              <span className="cursor-blink inline-block h-4 w-2 bg-volt align-middle" />
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
